/* Phase 35 — Plating Intelligence wizard.
 *
 * This component drives the 5-step flow (cuisine → plate → ratio → count →
 * generate) and renders the text + image output. It is reused by:
 *   • <PlatingIntelligencePage />       — full-page version (sidebar entry)
 *   • <PlatingModal />                  — slide-over from recipe card button
 *   • <PlatingTab />                    — embedded tab inside recipe detail
 *
 * IMPORTANT: never expose model, vendor, or provider names in any string here.
 * The user-facing actor is always "Plating Intelligence".
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Download, Trash2, Image as ImgIcon, Sparkles } from "lucide-react";
import toast from "react-hot-toast";
import api, { formatApiError } from "../api";
import { Button, Card, Badge, Spinner, EmptyState } from "./ui/Primitives";

const ASPECT_ICON = {
  "1:1":  { w: 28, h: 28 },
  "4:5":  { w: 24, h: 30 },
  "9:16": { w: 18, h: 32 },
  "16:9": { w: 32, h: 18 },
};

function StepLabel({ n, title }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand/15 text-brand text-[11px] font-semibold">{n}</span>
      <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">{title}</span>
    </div>
  );
}

function downloadBlob(b64, filename) {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const blob = new Blob([arr], { type: "image/png" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function slugify(s) {
  return (s || "rezept").toLowerCase().normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

export default function PlatingWizard({ recipe, onSaved, onClose, compact = false }) {
  const [meta, setMeta] = useState(null);
  const [cuisine, setCuisine] = useState("klassisch_at");
  const [plate, setPlate] = useState("rund");
  const [aspect, setAspect] = useState("1:1");
  const [imageCount, setImageCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState([]);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    api.get("/plating/meta")
      .then(({ data }) => { if (mounted.current) setMeta(data); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    if (!recipe?.id) return;
    api.get(`/plating/recipe/${recipe.id}`)
      .then(({ data }) => { if (mounted.current) setSaved(data.items || []); })
      .catch(() => {});
  }, [recipe?.id]);

  const cost = useMemo(() => {
    if (!meta) return 0;
    const row = (meta.pricing || []).find((p) => p.image_count === imageCount);
    return row?.credits || 0;
  }, [meta, imageCount]);

  const available = meta?.credits?.available ?? 0;
  const canAfford = available >= cost;

  const generate = async () => {
    if (!recipe?.id) { toast.error("Kein Rezept ausgewählt"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/plating/generate", {
        recipe_id: recipe.id,
        cuisine_style: cuisine,
        plate_shape: plate,
        aspect_ratio: aspect,
        image_count: imageCount,
      });
      if (!mounted.current) return;
      setResult(data);
      setSaved((prev) => [data, ...prev]);
      // Refresh meta credits
      const { data: m } = await api.get("/plating/meta");
      if (mounted.current) setMeta(m);
      if (onSaved) onSaved(data);
      toast.success("Plating Intelligence hat deine Vorschläge erstellt.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const downloadOne = (img, idx) => {
    const fname = `plating_${slugify(recipe?.name)}_${new Date().toISOString().slice(0, 10)}_${idx + 1}.jpg`;
    downloadBlob(img.b64, fname);
  };
  const downloadAll = (item) => {
    (item.images || []).forEach((img, i) => downloadOne(img, i));
  };
  const removeSaved = async (id) => {
    if (!window.confirm("Diese Plating-Vorschläge löschen?")) return;
    try {
      await api.delete(`/plating/${id}`);
      setSaved((prev) => prev.filter((s) => s.id !== id));
      if (result?.id === id) setResult(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!meta) return <div className="p-6"><Spinner /></div>;

  const cuisines = meta.cuisine_styles || [];
  const plates = meta.plate_shapes || [];
  const aspects = meta.aspect_ratios || [];

  return (
    <div className={compact ? "space-y-5" : "space-y-6"} data-testid="plating-wizard">
      {/* Header credits */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-brand" />
          <span className="text-sm font-medium">Plating Intelligence</span>
        </div>
        <Badge tone={available >= 5 ? "ok" : "warn"} data-testid="plating-credit-badge">
          {available} Plating-Credits
        </Badge>
      </div>

      {/* Step 1 — Cuisine */}
      <div>
        <StepLabel n={1} title="Küchenstil" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {cuisines.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCuisine(c.id)}
              data-testid={`plating-cuisine-${c.id}`}
              className={`px-3 py-2.5 rounded-lg border text-sm text-left transition ${
                cuisine === c.id
                  ? "border-brand bg-brand/10 text-brand"
                  : "border-white/10 hover:border-white/25"
              }`}
            >{c.label}</button>
          ))}
        </div>
      </div>

      {/* Step 2 — Plate shape */}
      <div>
        <StepLabel n={2} title="Tellerform" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {plates.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPlate(p.id)}
              data-testid={`plating-plate-${p.id}`}
              className={`px-3 py-3 rounded-lg border text-sm flex flex-col items-center gap-1.5 transition ${
                plate === p.id ? "border-brand bg-brand/10 text-brand" : "border-white/10 hover:border-white/25"
              }`}
            >
              <PlateIcon shape={p.id} active={plate === p.id} />
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Step 3 — Aspect ratio */}
      <div>
        <StepLabel n={3} title="Bildformat" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {aspects.map((a) => {
            const ic = ASPECT_ICON[a.id] || { w: 24, h: 24 };
            const active = aspect === a.id;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => setAspect(a.id)}
                data-testid={`plating-aspect-${a.id.replace(":", "x")}`}
                className={`px-3 py-3 rounded-lg border text-sm flex flex-col items-center gap-1.5 transition ${
                  active ? "border-brand bg-brand/10 text-brand" : "border-white/10 hover:border-white/25"
                }`}
              >
                <div
                  className={`rounded-sm border ${active ? "border-brand bg-brand/30" : "border-ink-muted/50 bg-white/5"}`}
                  style={{ width: ic.w, height: ic.h }}
                />
                <span>{a.id} · {a.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 4 — Image count */}
      <div>
        <StepLabel n={4} title="Anzahl Bilder" />
        <div className="grid grid-cols-2 gap-2">
          {(meta.pricing || []).map((p) => (
            <button
              key={p.image_count}
              type="button"
              onClick={() => setImageCount(p.image_count)}
              data-testid={`plating-count-${p.image_count}`}
              className={`px-4 py-3 rounded-lg border text-left transition ${
                imageCount === p.image_count ? "border-brand bg-brand/10 text-brand" : "border-white/10 hover:border-white/25"
              }`}
            >
              <div className="text-sm font-medium">{p.image_count} Bild{p.image_count > 1 ? "er" : ""} + Text</div>
              <div className="text-xs text-ink-muted">{p.credits} Credits</div>
            </button>
          ))}
        </div>
      </div>

      {/* Step 5 — Generate */}
      <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
        <div className="text-sm text-ink-muted" data-testid="plating-total-cost">
          Gesamtkosten: <span className="text-ink font-medium">{cost} Credits</span> · verfügbar {available}
        </div>
        <Button
          onClick={generate}
          disabled={loading || !canAfford || !recipe?.id}
          data-testid="plating-generate-btn"
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="animate-spin" size={14} />
              Plating Intelligence arbeitet…
            </span>
          ) : (
            <span className="inline-flex items-center gap-2">
              <Sparkles size={14} />
              {cost} Credits · Vorschläge generieren
            </span>
          )}
        </Button>
      </div>
      {!canAfford && !loading && (
        <div className="text-xs text-warn">
          Nicht genug Plating-Credits. Bitte Plan upgraden oder Add-on buchen.
        </div>
      )}

      {/* Current result */}
      {result && (
        <Card className="p-4 space-y-4 border-brand/40" data-testid="plating-result">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Aktuelle Vorschläge</div>
            <Button onClick={() => downloadAll(result)} variant="ghost" data-testid="plating-download-all">
              <Download size={14} /> Alle herunterladen
            </Button>
          </div>
          <ImageGallery item={result} onDownload={downloadOne} />
          <SuggestionList suggestions={result.suggestions} />
        </Card>
      )}

      {/* Saved history */}
      {saved.length > 0 && (
        <div className="space-y-3">
          <div className="text-xs font-medium uppercase tracking-wide text-ink-muted">Gespeicherte Vorschläge</div>
          {saved.map((s) => (
            <Card key={s.id} className="p-3 space-y-2" data-testid={`plating-history-${s.id}`}>
              <div className="flex items-center justify-between text-xs text-ink-muted gap-2 flex-wrap">
                <span>
                  {new Date(s.created_at).toLocaleString("de-AT")} · {s.image_count} Bilder · {s.aspect_ratio}
                </span>
                <div className="flex gap-2">
                  <Button onClick={() => downloadAll(s)} variant="ghost" size="sm">
                    <Download size={12} /> Download
                  </Button>
                  <Button onClick={() => removeSaved(s.id)} variant="ghost" size="sm" data-testid={`plating-delete-${s.id}`}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
              <ImageGallery item={s} compact onDownload={downloadOne} />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ImageGallery({ item, onDownload, compact = false }) {
  const imgs = item?.images || [];
  if (!imgs.length) return null;
  return (
    <div className={`grid gap-3 ${compact ? "grid-cols-3" : "grid-cols-1 md:grid-cols-3"}`}>
      {imgs.map((img, i) => (
        <div key={img.id || i} className="relative group rounded-lg overflow-hidden border border-white/10 bg-white/5">
          <img
            src={`data:${img.mime || "image/png"};base64,${img.b64}`}
            alt={`Plating ${i + 1}`}
            className="w-full h-auto block"
            data-testid={`plating-image-${i}`}
          />
          <button
            type="button"
            onClick={() => onDownload(img, i)}
            data-testid={`plating-download-${i}`}
            className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-bg/80 backdrop-blur border border-white/10 opacity-0 group-hover:opacity-100 transition"
          >
            <Download size={11} /> JPG
          </button>
        </div>
      ))}
    </div>
  );
}

function SuggestionList({ suggestions = [] }) {
  if (!suggestions.length) return null;
  return (
    <div className="space-y-3">
      {suggestions.map((s, i) => (
        <div key={i} className="rounded-lg border border-white/10 p-3" data-testid={`plating-suggestion-${i}`}>
          <div className="text-sm font-semibold mb-1">Vorschlag {i + 1}</div>
          <ul className="text-sm space-y-1">
            <li><span className="text-ink-muted">Anordnung: </span>{s.arrangement}</li>
            <li><span className="text-ink-muted">Garnitur: </span>{s.garnish}</li>
            <li><span className="text-ink-muted">Sauce: </span>{s.sauce}</li>
            <li><span className="text-ink-muted">Höhe & Textur: </span>{s.height_texture}</li>
            <li><span className="text-ink-muted">Profi-Tipp: </span>{s.pro_tip}</li>
          </ul>
        </div>
      ))}
    </div>
  );
}

function PlateIcon({ shape, active }) {
  const cls = active ? "stroke-brand" : "stroke-ink-muted";
  if (shape === "rund") return (
    <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><ellipse cx="16" cy="10" rx="13" ry="8" className={cls} strokeWidth="1.5" /></svg>
  );
  if (shape === "rechteckig") return (
    <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><rect x="3" y="4" width="26" height="12" rx="2" className={cls} strokeWidth="1.5" /></svg>
  );
  if (shape === "slate") return (
    <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><path d="M3 6 L29 4 L29 16 L3 14 Z" className={cls} strokeWidth="1.5" fill="none" /></svg>
  );
  return ( // bowl
    <svg width="32" height="20" viewBox="0 0 32 20" fill="none"><path d="M3 6 Q16 22 29 6" className={cls} strokeWidth="1.5" fill="none" /><line x1="3" y1="6" x2="29" y2="6" className={cls} strokeWidth="1.5" /></svg>
  );
}
